import { useEffect, useState } from 'react';

// 🔹 Tipos para cookies opcionales
export interface OptionalCookies {
  analytics: boolean;       // Cookies de análisis
  personalization: boolean; // Cookies de personalización
  ads: boolean;             // Cookies de anuncios
}

// 🔹 Función para obtener el valor de una cookie
const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

// 🔹 Función para crear/actualizar una cookie
const setCookie = (name: string, value: string, maxAgeSeconds = 31536000) => {
  // maxAge por defecto: 1 año
  document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}`;
};

// 🔹 Hook principal para gestionar el consentimiento de cookies
export default function useCookieConsent() {
  // Estado principal para saber si el usuario aceptó/rechazó cookies
  const [cookiesAccepted, setCookiesAccepted] = useState<null | boolean>(null);
  const [loadingCookies, setLoadingCookies] = useState(true); // Indica si aún se están leyendo cookies

  // 🔄 Leer cookies al montar el componente
  useEffect(() => {
    const accepted = getCookie('cookiesAccepted'); // Leer cookie principal
    if (accepted === 'true') setCookiesAccepted(true);
    else if (accepted === 'false') setCookiesAccepted(false);
    else setCookiesAccepted(null); // Nunca respondido
    setLoadingCookies(false); // Cookies ya leídas
  }, []);

  // 🔹 Función para aceptar cookies opcionales
  const acceptCookies = (optionalCookies: OptionalCookies) => {
    setCookie('cookiesAccepted', 'true'); // Cookie principal
    setCookie('analytics', optionalCookies.analytics.toString());
    setCookie('personalization', optionalCookies.personalization.toString());
    setCookie('ads', optionalCookies.ads.toString());
    setCookiesAccepted(true); // Actualizar estado
  };

  // 🔹 Función para rechazar cookies
  const rejectCookies = () => {
    setCookie('cookiesAccepted', 'false'); // Solo marcamos rechazo
    setCookiesAccepted(false);
  };

  // 🔹 Función para leer todas las cookies como objeto
  const parseCookies = (): Record<string, string> =>
    Object.fromEntries(
      document.cookie.split('; ').map(cookie => {
        const [key, value] = cookie.split('=');
        return [key, decodeURIComponent(value)];
      })
    );

  // 🔹 Retornamos estados y funciones
  return { 
    cookiesAccepted, // null = nunca respondió, true = aceptó, false = rechazó
    acceptCookies,   // Función para aceptar cookies opcionales
    rejectCookies,   // Función para rechazar cookies
    loadingCookies,  // true mientras se leen cookies
    parseCookies     // Devuelve todas las cookies como objeto
  };
}
